import { AnchorHTMLAttributes, useCallback, useEffect, useRef } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { useDarkTheme } from "@/libraries/design/utils/dark-theme-provider";

import { TabSize } from "./tabs-section";

export type TabProps = {
    tabId?: string;
    index?: number;
    title: string;
    isActive?: boolean;
    onTabSelect?: (tabId: string) => void;
    tabSize: TabSize;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href">;

const listItemStyles = css({
    position: "relative",
    zIndex: 1,
    scrollSnapAlign: "start",
    display: "flex",
    listStyle: "none",
    cursor: "pointer"
});

const tabLinkStyles = css({
    "display": "flex",
    "flex": 1,
    "alignItems": "center",
    "justifyContent": "center",
    "paddingBlock": token("space.100"),
    "whiteSpace": "nowrap",
    "borderRadius": wacToken("corner.radius.pill"),
    "textDecoration": "none",
    "cursor": "pointer",
    "transition": "color 0.3s ease-in-out",
    "outline": "none",
    // Prevent accidental text selection or native link-drag from suppressing
    // the click event when the cursor moves slightly during a tap/click.
    "userSelect": "none",
    "WebkitUserDrag": "none",
    "@media (prefers-reduced-motion: reduce)": {
        transition: "none"
    },
    "fontWeight": "500"
});

const inactiveTabStyles = css({
    "color": wacToken("color.primary.black"),
    [media.below.sm]: {
        backgroundColor: wacToken("color.neutral.light200")
    },
    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.black")}`,
        outlineOffset: "-2px"
    },
    "&:hover": {
        transition: "none",
        color: wacToken("color.primary.blue")
    }
});

const darkThemeInactiveTabStyles = css({
    "color": wacToken("color.neutral.light0"),
    [media.below.sm]: {
        backgroundColor: wacToken("color.neutral.dark200")
    },
    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.neutral.light0")}`,
        outlineOffset: "-2px"
    },
    "&:hover": {
        transition: "none",
        color: wacToken("color.extended.blue300")
    }
});

const activeTabStyles = css({
    "color": wacToken("color.neutral.light0"),
    [media.below.sm]: {
        backgroundColor: wacToken("color.primary.black")
    },
    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.neutral.light0")}`,
        outlineOffset: "-4px"
    }
});

const darkThemeActiveTabStyles = css({
    "color": wacToken("color.primary.black"),
    [media.below.sm]: {
        backgroundColor: wacToken("color.neutral.light0")
    },
    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.black")}`,
        outlineOffset: "-4px"
    }
});

const largeTabSizingStyles = css({
    fontSize: "18px",
    lineHeight: "26px",
    paddingInline: token("space.300"),
    [media.above.md]: {
        fontSize: "20px",
        lineHeight: "28px",
        paddingInline: token("space.400")
    },
    [media.above.lg]: {
        fontSize: "24px",
        lineHeight: "32px",
        paddingInline: token("space.500")
    }
});

const smallTabSizingStyles = css({
    fontSize: "16px",
    lineHeight: "24px",
    paddingInline: token("space.250"),
    [media.above.md]: {
        paddingInline: "28px"
    },
    [media.above.lg]: {
        fontSize: "18px",
        lineHeight: "26px",
        paddingInline: token("space.400")
    }
});

export const Tab = ({
    tabId: tabIdProp,
    index = 0,
    title,
    isActive = false,
    onTabSelect,
    tabSize
}: TabProps) => {
    const { isDarkTheme } = useDarkTheme();

    const tabId = tabIdProp ?? `tab-${index}`;
    const tabRef = useRef<HTMLAnchorElement>(null);
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            createAnalyticsEvent({
                eventType: "ui",
                action: "clicked",
                actionSubject: "tab",
                actionSubjectId: "wacRefreshTab",
                source: "tabsMenu",
                attributes: { label: title, tabId, tabIndex: index }
            }).fire();
            onTabSelect?.(tabId);
        },
        [onTabSelect, tabId, title, index, createAnalyticsEvent]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLAnchorElement>) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onTabSelect?.(tabId);
                return;
            }

            const currentTab = tabRef.current;
            const tabList = currentTab?.closest('[role="tablist"]');
            if (!tabList || !currentTab) {
                return;
            }

            const tabs = Array.from(
                tabList.querySelectorAll<HTMLElement>('[role="tab"]')
            );
            const currentIndex = tabs.indexOf(currentTab);

            if (currentIndex === -1) {
                return;
            }

            let nextIndex: number | null = null;

            switch (e.key) {
                case "ArrowRight":
                    nextIndex = (currentIndex + 1) % tabs.length;
                    break;
                case "ArrowLeft":
                    nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
                    break;
                case "Home":
                    nextIndex = 0;
                    break;
                case "End":
                    nextIndex = tabs.length - 1;
                    break;
                default:
                    return;
            }

            e.preventDefault();
            tabs[nextIndex]?.click();
        },
        [onTabSelect, tabId]
    );

    // Track previous active state to only focus on changes, not initial mount
    const wasActive = useRef(isActive);

    useEffect(() => {
        if (isActive && !wasActive.current && tabRef.current) {
            tabRef.current.focus();
        }
        wasActive.current = isActive;
    }, [isActive]);

    return (
        <li css={listItemStyles} data-tab-id={tabId} role="presentation">
            <a
                ref={tabRef}
                href={`#${tabId}`}
                css={[
                    tabLinkStyles,
                    tabSize === "large" && largeTabSizingStyles,
                    tabSize === "small" && smallTabSizingStyles,
                    isActive
                        ? isDarkTheme
                            ? darkThemeActiveTabStyles
                            : activeTabStyles
                        : isDarkTheme
                        ? darkThemeInactiveTabStyles
                        : inactiveTabStyles
                ]}
                role="tab"
                id={`tab-${tabId}`}
                aria-controls={tabId}
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                // Disable the browser's built-in link-drag behavior. Without
                // this, mousedown + small movement on the anchor fires
                // `dragstart` instead of `click`, silently swallowing the
                // tab selection.
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                data-event-container="tab"
                data-event-id={tabId}
                data-event-label={title}
            >
                {title}
            </a>
        </li>
    );
};
